import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { forwardRef, ReactNode, RefObject, useImperativeHandle, useRef } from 'react';
import { useEffectCustom } from '../hooks/use-effect-custom';
import { NativeProps, withNativeProps } from '../utils/native-props';
import './style.less';
import { nearest } from './utils/nearest';

export type SwipeActionRefApi = {
  close: () => void;
  show: (side?: 'left' | 'right') => void;
};

export type Action = {
  key: string | number;
  text: ReactNode;
  color?: string;
  onClick?: (action: Action, e: React.MouseEvent) => void;
};

export type SwipeActionProps = {
  rightActions?: Action[];
  leftActions?: Action[];
  onAction?: (action: Action) => void;
  closeOnTouchAway?: boolean;
  closeOnAction?: boolean;
  children: ReactNode;
} & NativeProps;

const defaultProps = {
  rightActions: [] as Action[],
  leftActions: [] as Action[],
  closeOnTouchAway: true,
  closeOnAction: true,
};

export const SwipeAction = forwardRef<SwipeActionRefApi, SwipeActionProps>((p, ref) => {
  const props = { ...defaultProps, ...p };

  const rootRef = useRef<HTMLDivElement>(null);

  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  function getWidth(ref: RefObject<HTMLDivElement>) {
    const element = ref.current;
    if (!element) return 0;
    return element.offsetWidth;
  }
  function getLeftWidth() {
    return getWidth(leftRef);
  }
  function getRightWidth() {
    return getWidth(rightRef);
  }

  const [{ x }, api] = useSpring(
    () => ({
      x: 0,
      config: { tension: 200, friction: 30 },
    }),
    [],
  );

  const draggingRef = useRef(false);

  const bind = useDrag(
    (state) => {
      draggingRef.current = true;
      const [offsetX] = state.offset;
      if (state.last) {
        const leftWidth = getLeftWidth();
        const rightWidth = getRightWidth();
        let position = offsetX + state.velocity[0] * state.direction[0] * 50;
        if (offsetX > 0) {
          position = Math.max(0, position);
        } else if (offsetX < 0) {
          position = Math.min(0, position);
        } else {
          position = 0;
        }
        console.log('xxx11', nearest([-rightWidth, 0, leftWidth], position));
        api.start({
          x: nearest([-rightWidth, 0, leftWidth], position),
        });
        window.setTimeout(() => {
          draggingRef.current = false;
        });
      } else {
        // api.start({
        //   x: offsetX,
        //   immediate: true,
        // });
      }
    },
    {
      from: () => [x.get(), 0],
      bounds: () => {
        const leftWidth = getLeftWidth();
        const rightWidth = getRightWidth();
        return {
          left: -rightWidth,
          right: leftWidth,
        };
      },
      axis: 'x',
      preventScroll: true,
    },
  );

  function close() {
    api.start({
      x: 0,
    });
  }

  useImperativeHandle(ref, () => ({
    show: (side: 'left' | 'right' = 'right') => {
      if (side === 'right') {
        api.start({
          x: -getRightWidth(),
        });
      } else if (side === 'left') {
        api.start({
          x: getLeftWidth(),
        });
      }
    },
    close,
  }));

  useEffectCustom(() => {
    if (!props.closeOnTouchAway) return;
    function handle(e: Event) {
      if (x.get() === 0) {
        return;
      }
      const root = rootRef.current;
      if (root && !root.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener('pointerdown', handle);
    return () => {
      document.removeEventListener('pointerdown', handle);
    };
  }, [props.closeOnTouchAway]);

  function renderAction(action: Action) {
    return (
      <div
        key={action.key}
        className="v-swipe-action-action-button"
        style={action.color ? { backgroundColor: action.color } : {}}
        onClick={(e) => {
          action.onClick?.(action, e);
          props.onAction?.(action);
          if (props.closeOnAction) {
            setTimeout(close, 50);
          }
        }}
      >
        {action.text}
      </div>
    );
  }

  x.to((v) => {
    console.log('xxx', v);
  });

  return withNativeProps(
    props,
    <div
      className="v-swipe-action"
      {...bind()}
      ref={rootRef}
      onClickCapture={(e) => {
        if (draggingRef.current) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      <animated.div className="v-swipe-action-track" style={{ x }}>
        <div className="v-swipe-action-actions v-swipe-action-actions-left" ref={leftRef}>
          {props.leftActions.map(renderAction)}
        </div>
        <div
          className="v-swipe-action-content"
          onClickCapture={(e) => {
            if (x.goal !== 0) {
              e.preventDefault();
              e.stopPropagation();
              api.start({
                x: 0,
              });
            }
          }}
        >
          <animated.div
            style={{
              pointerEvents: x.to((v) => {
                console.log('xxxx', v !== 0 && x.goal !== 0 ? 'none' : 'unset');
                return v !== 0 && x.goal !== 0 ? 'none' : 'unset';
              }),
            }}
          >
            {props.children}
          </animated.div>
        </div>
        <div className="v-swipe-action-actions v-swipe-action-actions-right" ref={rightRef}>
          {props.rightActions.map(renderAction)}
        </div>
      </animated.div>
    </div>,
  );
});
